import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { 
  PieChart, 
  TrendingUp, 
  Calculator, 
  Share2, 
  Download, 
  Info,
  AlertTriangle,
  DollarSign,
  ArrowRight
} from 'lucide-react';
import { 
  profiles, 
  defaultRates, 
  basketTooltips, 
  calculatePortfolioProjections,
  generateTimeSeriesData,
  saveToLocalStorage,
  loadFromLocalStorage,
  generateShareLink,
  chartColors,
  generateExportData,
  exportToCSV,
  exportToExcel,
  exportToPDF,
  migrateJarvisToJairvis
} from '../data/mockData';
import { BRAND_COLORS } from '../utils/branding';
import { useLanguage } from '../hooks/useLanguage';
import { LANGS } from '../utils/i18n';
import AllocationPieChart from './AllocationPieChart';
import ProjectionLineChart from './ProjectionLineChart';
import ResultsTable from './ResultsTable';
import AIBasketSplit from './AIBasketSplit';
import CapitalFlow from './CapitalFlow';
import PortfolioCard from './PortfolioCard';
import ScenarioMultiverse from './ScenarioMultiverse';
import TopBar from './TopBar';
import DisclaimerCard from './DisclaimerCard';
import ExportWarningDialog from './ExportWarningDialog';
import HowItWorksSection from './HowItWorksSection';
import PerformanceCardPreview from './PerformanceCardPreview';

const DCAPortfolio360 = () => {
  const { currentLang, t } = useLanguage();
  const navigate = useNavigate();
  
  // MIGRATION: Run migration on app load
  React.useEffect(() => {
    migrateJarvisToJairvis();
  }, []);
  
  // State management
  const [totalCapital, setTotalCapital] = useState(loadFromLocalStorage('dca_capital', 10000));
  const [monthlyContribution, setMonthlyContribution] = useState(loadFromLocalStorage('dca_monthly_contribution', 0));
  const [selectedProfile, setSelectedProfile] = useState(loadFromLocalStorage('dca_profile', 'balanced'));
  const [lastSelectedPreset, setLastSelectedPreset] = useState(loadFromLocalStorage('dca_last_preset', 'balanced'));
  const [allocations, setAllocations] = useState(loadFromLocalStorage('dca_allocations', profiles.balanced.allocations));
  const [aiSplit, setAiSplit] = useState(loadFromLocalStorage('dca_ai_split', profiles.balanced.aiSplit));
  const [rates, setRates] = useState(loadFromLocalStorage('dca_rates', defaultRates));
  const [selectedTimeframe, setSelectedTimeframe] = useState(loadFromLocalStorage('dca_selected_timeframe', '365'));
  const [projections, setProjections] = useState({});
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [allocationSum, setAllocationSum] = useState(100);
  const [isCustomProfile, setIsCustomProfile] = useState(false);
  const [customDefault, setCustomDefault] = useState(loadFromLocalStorage('dca_custom_default', null));
  
  // Export warning dialog state
  const [showExportWarning, setShowExportWarning] = useState(false);
  const [pendingExportType, setPendingExportType] = useState(null);

  // Calculate projections whenever inputs change
  useEffect(() => {
    if (totalCapital > 0) {
      const newProjections = calculatePortfolioProjections(totalCapital, allocations, aiSplit, rates, monthlyContribution);
      setProjections(newProjections);
      
      const timeSeriesData = generateTimeSeriesData(totalCapital, allocations, aiSplit, rates, parseInt(selectedTimeframe), monthlyContribution);
      setTimeSeriesData(timeSeriesData);
    }
  }, [totalCapital, monthlyContribution, allocations, aiSplit, rates, selectedTimeframe]);

  // Initialize custom default on load
  useEffect(() => {
    if (customDefault) {
      setAllocations(customDefault);
      setSelectedProfile('custom');
      setIsCustomProfile(true);
    }
  }, []);

  // Check allocation sum and detect custom profile
  useEffect(() => {
    // Calculate sum using new 3-basket structure
    const dcaCryptoVal = allocations.dcaCrypto !== undefined 
      ? allocations.dcaCrypto 
      : ((allocations.bitcoin || 0) + (allocations.diversified || 0));
    const sum = dcaCryptoVal + (allocations.aiTrading || 0) + (allocations.web3 || 0);
    setAllocationSum(sum);
    
    // Check if current allocations match any preset exactly (3 baskets)
    let matchedProfile = null;
    Object.entries(profiles).forEach(([profileKey, profile]) => {
      const profileDca = profile.allocations.dcaCrypto;
      const profileAi = profile.allocations.aiTrading;
      const profileWeb3 = profile.allocations.web3;
      
      const isExactMatch = 
        Math.abs(dcaCryptoVal - profileDca) <= 0.1 &&
        Math.abs((allocations.aiTrading || 0) - profileAi) <= 0.1 &&
        Math.abs((allocations.web3 || 0) - profileWeb3) <= 0.1;
      
      if (isExactMatch) {
        matchedProfile = profileKey;
      }
    });
    
    if (matchedProfile) {
      setSelectedProfile(matchedProfile);
      setIsCustomProfile(false);
    } else {
      setSelectedProfile('custom');
      setIsCustomProfile(true);
    }
  }, [allocations]);

  // Save to localStorage whenever state changes
  useEffect(() => {
    saveToLocalStorage('dca_capital', totalCapital);
    saveToLocalStorage('dca_monthly_contribution', monthlyContribution);
    saveToLocalStorage('dca_profile', selectedProfile);
    saveToLocalStorage('dca_last_preset', lastSelectedPreset);
    saveToLocalStorage('dca_allocations', allocations);
    saveToLocalStorage('dca_ai_split', aiSplit);
    saveToLocalStorage('dca_rates', rates);
    saveToLocalStorage('dca_is_custom_profile', isCustomProfile);
    saveToLocalStorage('dca_custom_default', customDefault);
    saveToLocalStorage('dca_selected_timeframe', selectedTimeframe);
  }, [totalCapital, monthlyContribution, selectedProfile, lastSelectedPreset, allocations, aiSplit, rates, isCustomProfile, customDefault, selectedTimeframe]);

  const handleProfileChange = (profileKey) => {
    if (profileKey === 'custom') {
      // Custom button clicked - do nothing (it's just a state indicator)
      return;
    }
    
    setSelectedProfile(profileKey);
    setLastSelectedPreset(profileKey);
    setAllocations({ ...profiles[profileKey].allocations });
    setAiSplit({ ...profiles[profileKey].aiSplit });
    setIsCustomProfile(false);
  };

  const handleAllocationChange = (basket, value) => {
    setAllocations(prev => ({
      ...prev,
      [basket]: value[0]
    }));
    // Custom profile detection will be handled by useEffect
  };

  const resetToPreset = () => {
    if (lastSelectedPreset && profiles[lastSelectedPreset]) {
      setAllocations({ ...profiles[lastSelectedPreset].allocations });
      setSelectedProfile(lastSelectedPreset);
      setIsCustomProfile(false);
    }
  };

  const saveAsDefault = () => {
    setCustomDefault({ ...allocations });
    // Show some feedback to user
    alert(`${t('saveAsDefault')} - ${t('appName')}`);
  };

  const handleTimeframeChange = (newTimeframe) => {
    setSelectedTimeframe(newTimeframe);
  };

  const normalizeAllocations = () => {
    // Get current values using new structure
    const dcaCryptoVal = allocations.dcaCrypto !== undefined 
      ? allocations.dcaCrypto 
      : ((allocations.bitcoin || 0) + (allocations.diversified || 0));
    const aiTradingVal = allocations.aiTrading || 0;
    const web3Val = allocations.web3 || 0;
    
    const sum = dcaCryptoVal + aiTradingVal + web3Val;
    
    if (Math.abs(sum - 100) > 0.1) {
      const normalizedAllocations = {
        dcaCrypto: Math.round((dcaCryptoVal / sum) * 100 * 10) / 10,
        aiTrading: Math.round((aiTradingVal / sum) * 100 * 10) / 10,
        web3: Math.round((web3Val / sum) * 100 * 10) / 10
      };
      
      // Ensure exact 100% by adjusting the largest allocation
      const newSum = normalizedAllocations.dcaCrypto + normalizedAllocations.aiTrading + normalizedAllocations.web3;
      if (Math.abs(newSum - 100) > 0.01) {
        // Find largest and adjust
        const maxKey = Object.entries(normalizedAllocations)
          .reduce((a, b) => a[1] > b[1] ? a : b)[0];
        normalizedAllocations[maxKey] += 100 - newSum;
      }
      
      setAllocations(normalizedAllocations);
    }
  };

  const handleAiSplitChange = (bot, value) => {
    setAiSplit(prev => {
      const newSplit = {
        ...prev,
        [bot]: value[0]
      };
      
      // If updating with old jarvis key, also update jairvis for consistency
      if (bot === 'jarvis') {
        newSplit.jairvis = value[0];
        delete newSplit.jarvis; // Remove old key
      }
      
      return newSplit;
    });
  };

  const handleRateChange = (asset, value) => {
    setRates(prev => {
      const newRates = {
        ...prev,
        [asset]: {
          ...prev[asset],
          rate: parseFloat(value) || 0
        }
      };
      
      // If updating jairvis, also update jarvis for backward compatibility
      if (asset === 'jairvis' && prev.jarvis) {
        newRates.jarvis = {
          ...prev.jarvis,
          rate: parseFloat(value) || 0
        };
      }
      
      return newRates;
    });
  };

  // Compute blended annual rate for AI Trading basket based on current bot rates and split
  const aiTradingAnnualValue = () => {
    const sigmaiRate = (rates.sigmai?.rate || 0) / 100;
    const sugairRate = (rates.sugair?.rate || 0) / 100;
    const jairvisRate = (rates.jairvis?.rate || rates.jarvis?.rate || 0) / 100;
    const sSplit = (aiSplit.sigmai || 0) / 100;
    const uSplit = (aiSplit.sugair || 0) / 100;
    const jSplit = (aiSplit.jairvis !== undefined ? aiSplit.jairvis : (aiSplit.jarvis || 0)) / 100;
    // Blended monthly rate (weighted average)
    const blendedMonthly = sigmaiRate * sSplit + sugairRate * uSplit + jairvisRate * jSplit;
    // Convert to annual (compounded)
    const annual = (Math.pow(1 + blendedMonthly, 12) - 1) * 100;
    return annual;
  };

  // Edit annual AI Trading rate: apply equivalent monthly rate to all bots
  const handleAiTradingAnnualChange = (value) => {
    const annual = parseFloat(value) || 0;
    const monthlyRate = (Math.pow(1 + annual / 100, 1 / 12) - 1) * 100;
    setRates(prev => ({
      ...prev,
      sigmai: { ...prev.sigmai, rate: parseFloat(monthlyRate.toFixed(2)) },
      sugair: { ...prev.sugair, rate: parseFloat(monthlyRate.toFixed(2)) },
      jairvis: { ...prev.jairvis, rate: parseFloat(monthlyRate.toFixed(2)) },
      jarvis: prev.jarvis ? { ...prev.jarvis, rate: parseFloat(monthlyRate.toFixed(2)) } : prev.jarvis
    }));
  };

  const handleCopyShareLink = async () => {
    const config = {
      totalCapital,
      monthlyContribution,
      selectedProfile,
      lastSelectedPreset,
      allocations,
      aiSplit,
      rates,
      isCustomProfile,
      customDefault
    };
    const shareLink = generateShareLink(config) + `&lang=${currentLang}`;
    
    try {
      await navigator.clipboard.writeText(shareLink);
      alert(t('copyLink') + ' - ' + t('appName'));
    } catch (error) {
      console.error('Failed to copy link:', error);
      alert('Failed to copy link');
    }
  };

  // B) Data Export Functions with confirmation dialog
  const handleExportCSV = () => {
    setPendingExportType('csv');
    setShowExportWarning(true);
  };

  const handleExportExcel = () => {
    setPendingExportType('excel');
    setShowExportWarning(true);
  };
  
  const handleExportPDF = () => {
    setPendingExportType('pdf');
    setShowExportWarning(true);
  };
  
  const confirmExport = async () => {
    try {
      console.log('🚀 Confirm export called, type:', pendingExportType);
      const exportData = generateExportData(totalCapital, monthlyContribution, allocations, aiSplit, rates, projections, currentLang);
      
      if (pendingExportType === 'csv') {
        exportToCSV(exportData);
        console.log('✅ CSV exported');
      } else if (pendingExportType === 'excel') {
        await exportToExcel(exportData);
        console.log('✅ Excel exported');
      } else if (pendingExportType === 'pdf') {
        console.log('📄 Calling exportToPDF...');
        const result = await exportToPDF(totalCapital, monthlyContribution, allocations, aiSplit, rates, projections, currentLang);
        console.log('📄 PDF export result:', result);
        if (result) {
          console.log('✅ PDF exported successfully!');
        }
      }
    } catch (error) {
      console.error('❌ Export failed:', error);
      console.error('Error stack:', error.stack);
      alert(t('exportError') + ': ' + error.message);
    }
  };

  // Function to save snapshot and navigate to parcours
  const handleGoToParcours = () => {
    // Calculate dcaCrypto value for snapshot
    const dcaCryptoVal = allocations.dcaCrypto !== undefined 
      ? allocations.dcaCrypto 
      : ((allocations.bitcoin || 0) + (allocations.diversified || 0));
    
    // Save snapshot to localStorage with new structure
    const snapshot = {
      profile: selectedProfile,
      allocations: {
        dcaCrypto: dcaCryptoVal,
        aiTrading: allocations.aiTrading,
        web3: allocations.web3
      },
      aiSplit,
      updatedAt: Date.now()
    };
    localStorage.setItem('sim360_snapshot', JSON.stringify(snapshot));
    
    // Navigate to parcours
    navigate(`/parcours?lang=${currentLang}`);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const currentProjection = projections[parseInt(selectedTimeframe)] || {};

  // Translated basket tooltips - Updated for 3 baskets
  const getBasketTooltip = (basket) => {
    const tooltipKeys = {
      dcaCrypto: 'tooltipDcaCrypto',
      bitcoin: 'tooltipDcaCrypto', // backward compatibility
      diversified: 'tooltipDcaCrypto', // backward compatibility 
      aiTrading: 'tooltipAI',
      web3: 'tooltipWeb3'
    };
    return t(tooltipKeys[basket]);
  };

  // Translated basket names - Updated for 3 baskets
  const getBasketName = (basket) => {
    const nameKeys = {
      dcaCrypto: 'dcaCrypto',
      bitcoin: 'dcaCrypto', // backward compatibility
      diversified: 'dcaCrypto', // backward compatibility  
      aiTrading: 'aiTrading', 
      web3: 'web3'
    };
    return t(nameKeys[basket]);
  };

  return (
    <div 
      className="min-h-screen text-white"
      style={{ 
        background: BRAND_COLORS.GRADIENT_BACKGROUND,
        color: BRAND_COLORS.TEXT_PRIMARY 
      }}
    >
      {/* Top Bar */}
      <TopBar />
      
      {/* Main Content with top padding */}
      <div className="pt-6">
        {/* Header */}
        <div 
          className="border-b"
          style={{
            borderColor: BRAND_COLORS.BORDER_SUBTLE,
            backgroundColor: BRAND_COLORS.BACKGROUND_PRIMARY
          }}
        >
          <div className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 
                  className="text-3xl font-bold"
                  style={{ color: BRAND_COLORS.ACCENT_PRIMARY }}
                >
                  {t('appName')}
                </h1>
                <p 
                  className="mt-1"
                  style={{ color: BRAND_COLORS.TEXT_SECONDARY }}
                >
                  {t('appSubtitle')}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <Badge 
                  variant="outline" 
                  style={{
                    borderColor: BRAND_COLORS.ACCENT_PRIMARY,
                    color: BRAND_COLORS.ACCENT_PRIMARY,
                    backgroundColor: 'transparent',
                    backdropFilter: 'none'
                  }}
                >
                  <Calculator className="w-4 h-4 mr-1" />
                  {t('eduBadge')}
                </Badge>
                <Button
                  onClick={() => { window.location.href = '/myportafolio'; }}
                  className="bg-gradient-to-r from-[#00f4ff] to-[#12b7ff] text-[#020817] hover:brightness-110 rounded-full px-4 py-2 text-sm font-bold min-h-[40px] transition-all shadow-[0_0_18px_-4px_rgba(0,244,255,0.7)]"
                  data-testid="hero-myportafolio-btn"
                >
                  {t('myPortafolio')}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8 space-y-8">
        {/* Input Controls */}
        <Card 
          style={{
            backgroundColor: BRAND_COLORS.BACKGROUND_CARDS,
            borderColor: BRAND_COLORS.BORDER_DEFAULT,
            boxShadow: `0 25px 50px -12px ${BRAND_COLORS.BORDER_SUBTLE}`
          }}
        >
          <CardHeader>
            <CardTitle 
              className="flex items-center gap-2"
              style={{ color: BRAND_COLORS.ACCENT_PRIMARY }}
            >
              <DollarSign className="w-5 h-5" />
              {t('config')}
            </CardTitle>
            <p className="text-sm text-[#9EB4C0] mt-2 leading-relaxed">
              {t('configDescription')}
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Capital Input */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label 
                  htmlFor="capital" 
                  style={{ color: BRAND_COLORS.TEXT_PRIMARY }}
                >
                  {t('capital')}
                </Label>
                <Input
                  id="capital"
                  type="number"
                  value={totalCapital}
                  onChange={(e) => setTotalCapital(parseFloat(e.target.value) || 0)}
                  className="text-lg h-12"
                  style={{
                    backgroundColor: BRAND_COLORS.BACKGROUND_PRIMARY,
                    borderColor: BRAND_COLORS.BORDER_DEFAULT,
                    color: BRAND_COLORS.TEXT_PRIMARY
                  }}
                  placeholder="10000"
                />
              </div>
              
              <div className="space-y-2">
                <Label 
                  htmlFor="monthlyContribution" 
                  style={{ color: BRAND_COLORS.TEXT_PRIMARY }}
                >
                  {t('monthlyContributionLabel')}
                </Label>
                <Input
                  id="monthlyContribution"
                  type="number"
                  value={monthlyContribution}
                  onChange={(e) => setMonthlyContribution(parseFloat(e.target.value) || 0)}
                  className="text-lg h-12"
                  style={{
                    backgroundColor: BRAND_COLORS.BACKGROUND_PRIMARY,
                    borderColor: BRAND_COLORS.BORDER_DEFAULT,
                    color: BRAND_COLORS.TEXT_PRIMARY
                  }}
                  placeholder="0"
                />
                <p className="text-xs text-[#0677aa] leading-relaxed">
                  {t('monthlyContributionHelp')}
                </p>
              </div>
            </div>

            {/* Profile Selection */}
            <div className="space-y-3">
              <Label className="text-white">{t('profile')}</Label>
              <div className="flex gap-2 flex-wrap">
                {Object.entries(profiles).map(([key, profile]) => (
                  <Button
                    key={key}
                    variant={selectedProfile === key ? "default" : "outline"}
                    onClick={() => handleProfileChange(key)}
                    className={selectedProfile === key 
                      ? "bg-[#00f4ff] text-black hover:bg-[#12b7ff]"
                      : "border-[#0677aa] text-white hover:bg-[#0b1c38]"
                    }
                  >
                    {t(key)}
                  </Button>
                ))}
                
                {/* Custom Profile Button */}
                <Button
                  variant={selectedProfile === 'custom' ? "default" : "outline"}
                  onClick={() => handleProfileChange('custom')}
                  className={selectedProfile === 'custom'
                    ? "bg-[#00f4ff] text-black hover:bg-[#12b7ff]"
                    : "border-[#0677aa] text-white hover:bg-[#0b1c38]"
                  }
                  disabled={!isCustomProfile}
                >
                  {t('custom')}
                </Button>
              </div>
              
              {/* Custom Profile Hint and Actions */}
              {isCustomProfile && (
                <div className="space-y-2">
                  <p className="text-sm text-[#12b7ff] italic">
                    {t('customHint')}
                  </p>
                  <div className="flex gap-3 text-xs">
                    <button
                      onClick={resetToPreset}
                      className="text-[#00f4ff] hover:text-[#12b7ff] underline transition-colors"
                    >
                      {t('resetPreset')}
                    </button>
                    <span className="text-[#0677aa]">•</span>
                    <button
                      onClick={saveAsDefault}
                      className="text-[#00f4ff] hover:text-[#12b7ff] underline transition-colors"
                    >
                      {t('saveAsDefault')}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Allocation Warning */}
            {Math.abs(allocationSum - 100) > 0.1 && (
              <div className="flex items-center justify-between p-3 bg-red-900/20 border border-red-500/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  <span className="text-red-300">
                    {t('allocationsMustSum')} {allocationSum.toFixed(1)}%
                  </span>
                </div>
                <Button
                  onClick={normalizeAllocations}
                  size="sm"
                  className="bg-[#12b7ff] text-black hover:bg-[#0677aa] text-xs"
                >
                  {t('autoNormalize')}
                </Button>
              </div>
            )}

            {/* Portfolio Allocation Description */}
            <div className="p-4 rounded-lg bg-[#00f4ff]/5 border border-[#00f4ff]/20">
              <p className="text-sm text-[#9EB4C0] leading-relaxed">
                💡 {t('portfolioAllocationDescription')}
              </p>
            </div>

            {/* Allocation Sliders - 3 Baskets: DCA Bitcoin & Crypto, AI Trading, Web3 */}
            <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Basket 1: DCA Bitcoin & Crypto */}
              <div className="space-y-3 p-4 rounded-xl bg-gradient-to-br from-[#0b1c38]/80 to-[#040816]/80 border border-[#00f4ff]/20">
                <div className="flex items-center justify-between">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-2 cursor-help">
                          <span className="text-xl">₿</span>
                          <Label className="text-white font-semibold">{t('dcaCrypto')}</Label>
                          <Info className="w-4 h-4 text-[#0677aa]" />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="bg-[#0b1c38] border-[#12b7ff] text-white max-w-xs">
                        <p>{t('tooltipDcaCrypto')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <span className="text-[#00f4ff] font-bold text-lg">
                    {allocations.dcaCrypto ?? (allocations.bitcoin + allocations.diversified)}%
                  </span>
                </div>
                <Slider
                  value={[allocations.dcaCrypto ?? (allocations.bitcoin + allocations.diversified)]}
                  onValueChange={(value) => handleAllocationChange('dcaCrypto', value)}
                  max={100}
                  step={1}
                  className="w-full"
                />
                <p className="text-xs text-[#9EB4C0]">Panier 1</p>
              </div>

              {/* Basket 2: AI Trading */}
              <div className="space-y-3 p-4 rounded-xl bg-gradient-to-br from-[#0b1c38]/80 to-[#040816]/80 border border-[#00f4ff]/20">
                <div className="flex items-center justify-between">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-2 cursor-help">
                          <span className="text-xl">🤖</span>
                          <Label className="text-white font-semibold">{t('aiTrading')}</Label>
                          <Info className="w-4 h-4 text-[#0677aa]" />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="bg-[#0b1c38] border-[#12b7ff] text-white max-w-xs">
                        <p>{t('tooltipAI')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <span className="text-[#00f4ff] font-bold text-lg">
                    {allocations.aiTrading}%
                  </span>
                </div>
                <Slider
                  value={[allocations.aiTrading]}
                  onValueChange={(value) => handleAllocationChange('aiTrading', value)}
                  max={100}
                  step={1}
                  className="w-full"
                />
                <p className="text-xs text-[#9EB4C0]">Panier 2</p>
              </div>

              {/* Basket 3: Web3 */}
              <div className="space-y-3 p-4 rounded-xl bg-gradient-to-br from-[#0b1c38]/80 to-[#040816]/80 border border-[#00f4ff]/20">
                <div className="flex items-center justify-between">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-2 cursor-help">
                          <span className="text-xl">🌐</span>
                          <Label className="text-white font-semibold">{t('web3')}</Label>
                          <Info className="w-4 h-4 text-[#0677aa]" />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="bg-[#0b1c38] border-[#12b7ff] text-white max-w-xs">
                        <p>{t('tooltipWeb3')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <span className="text-[#00f4ff] font-bold text-lg">
                    {allocations.web3}%
                  </span>
                </div>
                <Slider
                  value={[allocations.web3]}
                  onValueChange={(value) => handleAllocationChange('web3', value)}
                  max={100}
                  step={1}
                  className="w-full"
                />
                <p className="text-xs text-[#9EB4C0]">Panier 3</p>
              </div>
            </div>

            {/* AI Trading Split */}
            {allocations.aiTrading > 0 && (
              <AIBasketSplit 
                aiSplit={aiSplit}
                onSplitChange={handleAiSplitChange}
                totalAiAllocation={allocations.aiTrading}
                totalCapital={totalCapital}
              />
            )}
          </CardContent>
        </Card>

        {/* Rate Assumptions */}
        <Card className="bg-[#0b1c38] border-[#12b7ff] shadow-xl">
          <CardHeader>
            <CardTitle className="text-[#00f4ff]">{t('assumptions')}</CardTitle>
            <div className="space-y-3 mt-3">
              <p className="text-sm text-[#9EB4C0] leading-relaxed">
                {t('assumptionsDescription')}
              </p>
              <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                <p className="text-sm text-yellow-200 leading-relaxed">
                  ⚠️ {t('assumptionsWarning')}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Show rates in order: dcaCrypto, sigmai, sugair, jairvis, web3 */}
              {['dcaCrypto', 'sigmai', 'sugair', 'jairvis', 'web3'].map((asset) => {
                const rateInfo = rates[asset];
                if (!rateInfo) return null;
                
                return (
                <div key={asset} className="space-y-2" data-testid={`rate-${asset}`}>
                  <div className="flex items-center gap-2">
                    <Label className="capitalize text-white">
                      {rateInfo.name}
                    </Label>
                    {/* D) AI Trading Performance Fee Tooltip */}
                    {rateInfo.hasPerformanceFee && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-4 h-4 text-[#0677aa] cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="bg-[#0b1c38] border-[#12b7ff] text-white max-w-xs">
                            <p>{t('aiPerformanceFeeTooltip')}</p>
                            <p className="text-xs mt-1">{t('grossRate')}: {rateInfo.grossRate}% → {t('netRate')}: {rateInfo.rate.toFixed(2)}%</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="0.01"
                      value={rateInfo.rate.toFixed(2)}
                      onChange={(e) => handleRateChange(asset, e.target.value)}
                      className="bg-[#040816] border-[#0677aa] text-white"
                      data-testid={`rate-input-${asset}`}
                    />
                    <span className="text-sm text-[#0677aa]">
                      % {t(rateInfo.period)}
                    </span>
                  </div>
                </div>
              );
              })}
            </div>

            {/* Aggregated AI Trading Annual Rate (editable) */}
            <div className="mt-6 p-4 rounded-xl bg-gradient-to-br from-[#00f4ff]/5 to-[#12b7ff]/5 border border-[#00f4ff]/20">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="space-y-1">
                  <Label className="text-[#00f4ff] font-semibold flex items-center gap-2">
                    🤖 {t('aiTradingAnnualLabel')}
                  </Label>
                  <p className="text-xs text-[#9EB4C0] max-w-md">
                    {t('aiTradingAnnualHelp')}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.1"
                    value={aiTradingAnnualValue().toFixed(2)}
                    onChange={(e) => handleAiTradingAnnualChange(e.target.value)}
                    className="bg-[#040816] border-[#00f4ff] text-[#00f4ff] font-bold w-28 text-right"
                    data-testid="rate-input-aiTradingAnnual"
                  />
                  <span className="text-sm text-[#00f4ff]">% {t('annual')}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Charts and Results */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Allocation Chart */}
          <AllocationPieChart 
            allocations={allocations}
            totalCapital={totalCapital}
            aiSplit={aiSplit}
          />
          
          {/* Projection Chart */}
          <ProjectionLineChart
            data={timeSeriesData}
            selectedTimeframe={selectedTimeframe}
            onTimeframeChange={handleTimeframeChange}
            currentProjection={currentProjection}
            totalCapital={totalCapital}
            monthlyContribution={monthlyContribution}
          />
        </div>

        {/* Capital Flow — animated Sankey diagram */}
        <CapitalFlow
          totalCapital={totalCapital}
          monthlyContribution={monthlyContribution}
          allocations={allocations}
          aiSplit={aiSplit}
          projections={projections}
        />

        {/* Portfolio Card — collectible shareable card */}
        <PortfolioCard
          profile={selectedProfile}
          totalCapital={totalCapital}
          monthlyContribution={monthlyContribution}
          allocations={allocations}
          aiSplit={aiSplit}
          projections={projections}
        />

        {/* Scenario Multiverse — compare pessimistic / realistic / optimistic */}
        <ScenarioMultiverse
          totalCapital={totalCapital}
          monthlyContribution={monthlyContribution}
          allocations={allocations}
          aiSplit={aiSplit}
          rates={rates}
        />

        {/* Results Table */}
        <ResultsTable 
          projections={projections}
          allocations={allocations}
          totalCapital={totalCapital}
          aiSplit={aiSplit}
          monthlyContribution={monthlyContribution}
        />

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 flex-wrap">
          <Button 
            onClick={handleCopyShareLink}
            className="bg-[#12b7ff] text-white hover:bg-[#0677aa]"
          >
            <Share2 className="w-4 h-4 mr-2" />
            {t('copyLink')}
          </Button>
          
          {/* B) Data Export Buttons */}
          <Button 
            onClick={handleExportCSV}
            variant="outline"
            className="border-[#00f4ff] text-[#00f4ff] hover:bg-[#00f4ff] hover:text-black"
          >
            <Download className="w-4 h-4 mr-2" />
            {t('exportCsv')}
          </Button>
          
          <Button 
            onClick={handleExportExcel}
            variant="outline"
            className="border-[#12b7ff] text-[#12b7ff] hover:bg-[#12b7ff] hover:text-black"
          >
            <Download className="w-4 h-4 mr-2" />
            {t('exportExcel')}
          </Button>
          
          <Button 
            onClick={handleExportPDF}
            variant="default"
            className="relative bg-gradient-to-r from-[#00f4ff] via-[#12b7ff] to-[#00f4ff] text-[#040816] font-bold shadow-2xl shadow-[#00f4ff]/40 hover:shadow-[#00f4ff]/60 hover:scale-105 transition-all duration-300 border-2 border-[#00f4ff]/20"
            size="lg"
          >
            <Download className="w-5 h-5 mr-2" />
            {t('exportPdf')}
            <Badge 
              variant="default" 
              className="ml-2 text-[9px] px-2 py-0.5 bg-gradient-to-r from-green-400 to-emerald-400 text-black border-0 shadow-md shadow-green-400/30 font-bold"
            >
              NEW
            </Badge>
          </Button>
        </div>

        {/* How It Works Section */}
        <HowItWorksSection />

        {/* My Portafolio Promo */}
        <Card
          className="mt-8 overflow-hidden border"
          style={{
            background: 'linear-gradient(135deg, rgba(0,244,255,0.06), rgba(124,58,237,0.08) 60%, rgba(255,48,48,0.04))',
            borderColor: 'rgba(0,244,255,0.30)',
            boxShadow: '0 18px 60px -25px rgba(0,244,255,0.45)',
          }}
          data-testid="myportafolio-promo"
        >
          <CardContent className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-8">
              {/* Animated Card Preview */}
              <div className="flex justify-center md:justify-start md:pl-2 shrink-0">
                <PerformanceCardPreview width={200} />
              </div>

              {/* Text + features */}
              <div className="flex-1">
                <div className="text-[11px] tracking-[0.35em] text-[#00f4ff] uppercase mb-2">
                  My Portafolio
                </div>
                <h3 className="text-2xl md:text-3xl font-black text-white leading-tight mb-3">
                  {t('portafolioPromoTitle')}
                </h3>
                <p className="text-[#9EB4C0] text-sm md:text-base mb-4 max-w-2xl">
                  {t('portafolioPromoLead')}
                </p>
                <ul className="space-y-1.5 text-sm text-[#cfe1f0]">
                  <li className="flex items-start gap-2">
                    <span className="text-[#00f4ff] mt-0.5">●</span> {t('portafolioPromoFeature1')}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#a78bfa] mt-0.5">●</span> {t('portafolioPromoFeature2')}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#22c55e] mt-0.5">●</span> {t('portafolioPromoFeature3')}
                  </li>
                </ul>
                <div className="mt-3 text-[10px] text-[#7c3aed] tracking-[0.25em] uppercase flex items-center gap-2">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#ff00aa] animate-pulse" />
                  Earn a collectible card — Common · Rare · Legendary · Mythic
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-col gap-3 md:min-w-[220px] shrink-0">
                <Button
                  onClick={() => { window.location.href = '/myportafolio?mode=register'; }}
                  className="bg-gradient-to-r from-[#00f4ff] to-[#12b7ff] text-[#020817] font-bold h-11 rounded-full shadow-[0_0_22px_-4px_rgba(0,244,255,0.7)] hover:brightness-110"
                  data-testid="promo-signup-btn"
                >
                  {t('portafolioPromoSignup')}
                </Button>
                <Button
                  onClick={() => { window.location.href = '/myportafolio?mode=login'; }}
                  variant="outline"
                  className="border-[#00f4ff]/50 text-[#00f4ff] hover:bg-[rgba(0,244,255,0.08)] h-11 rounded-full"
                  data-testid="promo-login-btn"
                >
                  {t('portafolioPromoLogin')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Call to Action - Go to Parcours */}
        <Card className="mt-8 bg-gradient-to-br from-[#0b1c38] to-[#0C1A2A] border-2 border-[#00f4ff]/20">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-bold mb-3 bg-gradient-to-r from-[#00f4ff] to-[#12b7ff] bg-clip-text text-transparent">
              {t('goToParcours')}
            </h3>
            <p className="text-[#9EB4C0] mb-6">
              {t('goToParcoursDesc')}
            </p>
            <Button
              onClick={handleGoToParcours}
              size="lg"
              className="bg-gradient-to-r from-[#00f4ff] to-[#12b7ff] text-[#040816] font-bold hover:shadow-xl hover:shadow-[#00f4ff]/40 transition-all duration-300"
            >
              {t('goToParcours')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </CardContent>
        </Card>

        {/* AI Trading Pole Vision */}
        <Card className="mt-6 bg-gradient-to-br from-[#1a1a2e] to-[#0C1A2A] border border-[#00f4ff]/30">
          <CardContent className="p-6">
            {/* AI Trading Pole Vision Block */}
            <div>
              <h4 className="text-lg font-bold text-[#00f4ff] mb-3 flex items-center gap-2">
                {t('aiPoleVisionTitle')}
              </h4>
              <div className="text-sm text-[#9EB4C0] space-y-3 leading-relaxed">
                <p>{t('aiPoleVisionText1')}</p>
                <p>{t('aiPoleVisionText2')}</p>
                <p className="text-[#00f4ff]/80 font-medium">{t('aiPoleVisionText3')}</p>
                <p className="bg-[#00f4ff]/10 p-3 rounded-lg border border-[#00f4ff]/20 text-[#00f4ff]">
                  💡 {t('aiPoleVisionText4')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* A) Replace inline disclaimer with DisclaimerCard component */}
        <DisclaimerCard />
        </div>
      </div>
      
      {/* Export Warning Dialog */}
      <ExportWarningDialog
        open={showExportWarning}
        onOpenChange={setShowExportWarning}
        onConfirm={confirmExport}
        exportType={pendingExportType}
      />
    </div>
  );
};

export default DCAPortfolio360;